import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from 'react';
import { Spin, Table, Tag, Tooltip } from "antd";
import { EmailMessageAddressType } from "../api/Representation";
import dayjs from "dayjs";
import CommunicationPurpose from "../enums/CommunicationPurpose";
import CommunicationType from "../enums/CommunicationType";
import { formatDateTime } from "../../../util/FormatUtil";
function CommunicationEmailRecipient({ recipient }) {
    const [showStatusMessage, setShowStatusMessage] = useState(false);
    function toggleShowDeliveryStatusMessage() {
        setShowStatusMessage(!showStatusMessage);
    }
    function buildTag(recipient) {
        if (recipient.deliveredAt && !recipient.suppressedAt && !recipient.deliveryFailedAt) {
            return _jsx(Tooltip, { placement: "right", title: "Delivered at " + formatDateTime(recipient.deliveredAt), children: _jsx(Tag, { color: "green", onClick: toggleShowDeliveryStatusMessage, children: "Delivered" }) });
        }
        if (recipient.suppressedAt) {
            return _jsx(Tooltip, { placement: "right", title: "Delivery suppressed at " + formatDateTime(recipient.suppressedAt), children: _jsx(Tag, { color: "orange", onClick: toggleShowDeliveryStatusMessage, children: "Suppressed" }) });
        }
        if (recipient.deliveryFailedAt) {
            return _jsx(Tooltip, { placement: "right", title: "Delivery failed at " + formatDateTime(recipient.deliveryFailedAt), children: _jsx(Tag, { color: "red", onClick: toggleShowDeliveryStatusMessage, children: "Delivery Failed" }) });
        }
        return null;
    }
    function formatType(type) {
        let strType = '' + type;
        if (strType.length <= 1) {
            return strType;
        }
        return strType.charAt(0).toUpperCase() + strType.slice(1).toLowerCase();
    }
    return (_jsxs("span", { children: [formatType(recipient.type), ": ", recipient.email, " ", buildTag(recipient), " ", _jsx("br", {}), showStatusMessage && _jsxs(_Fragment, { children: [_jsx("span", { style: { fontFamily: 'monospace' }, children: recipient.deliveryStatusMessage }), _jsx("br", {})] })] }));
}
function CommunicationEmail({ emailCommunication }) {
    return (_jsx("div", { children: _jsxs("p", { className: "no-margin", children: [emailCommunication.recipients?.map(recipient => _jsx(CommunicationEmailRecipient, { recipient: recipient }, recipient.id)), "Subject: ", emailCommunication.subject] }) }));
}
function CommunicationSms({ smsCommunication }) {
    const [showStatusMessage, setShowStatusMessage] = useState(false);
    function toggleShowDeliveryStatusMessage() {
        setShowStatusMessage(!showStatusMessage);
    }
    function buildTag(sms) {
        if (sms.deliveredAt && !sms.deliveryFailedAt) {
            return _jsx(Tooltip, { placement: "right", title: "Delivered at " + formatDateTime(sms.deliveredAt), children: _jsx(Tag, { color: "green", onClick: toggleShowDeliveryStatusMessage, children: "Delivered" }) });
        }
        if (sms.deliveryFailedAt) {
            return _jsx(Tooltip, { placement: "right", title: "Delivery failed at " + formatDateTime(sms.deliveryFailedAt), children: _jsx(Tag, { color: "red", onClick: toggleShowDeliveryStatusMessage, children: "Delivery Failed" }) });
        }
        return null;
    }
    const profileName = smsCommunication.driver?.tenantName ?? smsCommunication.tenant.name;
    return (_jsx("div", { children: _jsxs("p", { className: "no-margin", children: ["Mobile: ", smsCommunication.to, " ", buildTag(smsCommunication), _jsx("br", {}), showStatusMessage && _jsxs(_Fragment, { children: [_jsx("span", { style: { fontFamily: 'monospace' }, children: smsCommunication.deliveryStatusMessage }), _jsx("br", {})] }), smsCommunication.driver && _jsxs(_Fragment, { children: ["Driver: ", smsCommunication.driver.firstName, " ", smsCommunication.driver.lastName, _jsx("br", {})] }), profileName && _jsxs(_Fragment, { children: ["Profile: ", profileName, _jsx("br", {})] }), "Message: ", smsCommunication.message] }) }));
}
function CommunicationApp({ appCommunication }) {
    return (_jsx("div", { children: _jsxs("p", { className: "no-margin", children: [appCommunication.driver && _jsxs("span", { children: ["Driver: ", appCommunication.driver.firstName, " ", appCommunication.driver.lastName, _jsx("br", {}), "Profile: ", appCommunication.driver.tenantName, _jsx("br", {})] }), "Message: ", appCommunication.message] }) }));
}
/***********************************************************************************************************************
 *  Main table
 */
const tableColumns = [{
        title: 'Sent',
        dataIndex: 'sentAt',
        width: "10%",
        render: (text /*, record*/) => {
            return dayjs(text).format("DD/MM/YYYY HH:mm");
        }
    }, {
        title: 'Purpose',
        dataIndex: 'purpose',
        width: "10%",
        render: (text /*, communication: CommunicationsResponse*/) => {
            const purposeEnum = CommunicationPurpose.fromString(text);
            return purposeEnum ? purposeEnum.description : "-";
        }
    }, {
        title: 'Status',
        dataIndex: 'status',
        render: (text, communication) => {
            return _jsxs(_Fragment, { children: [text, _jsx("br", {}), communication?.statusMessage] });
        },
        width: "10%"
    }, {
        title: 'Type',
        dataIndex: 'type',
        width: "5%",
        render: (text /*, communication: CommunicationsResponse*/) => {
            const typeEnum = CommunicationType.fromString(text);
            return typeEnum ? typeEnum.description : "-";
        }
    }, {
        title: 'Details',
        key: "typeDetails",
        dataIndex: 'type',
        width: "65%",
        render: (type, communication) => {
            if (type == CommunicationType.APP) {
                return (_jsx(CommunicationApp, { appCommunication: communication }));
            }
            if (type == CommunicationType.SMS) {
                return (_jsx(CommunicationSms, { smsCommunication: communication }));
            }
            return (_jsx(CommunicationEmail, { emailCommunication: communication }));
        }
    }];
export default function CommunicationTable({ loading = false, communications }) {
    return (_jsx(Spin, { spinning: loading, children: _jsx(Table, { columns: tableColumns, size: "middle", rowKey: record => record.id, dataSource: communications, pagination: false }) }));
}
